import { ReactNode } from 'react';
import { ErrorBoundary, FallbackRender } from '@sentry/nextjs';
import axios, { AxiosError, AxiosResponse } from 'axios';
import { redirectSigninPage } from '@zep/common/routes';
import { logger } from '@zep/core-logger';
import { setApiNetworkLoggerWeb } from '@zep/next';
import { Error500PageView } from '@zep/web-components';

// unhandledrejection 은 Sentry 기본 globalHandlers 통합이 reason 을 그대로 캡처한다
// (mechanism: onunhandledrejection). 여기서 중복 리스너를 달지 않는다.
const GlobalErrorBoundary = ({
  children
}: Props) => {
  const fallback: FallbackRender = ({
    error,
    resetError
  }) => {
    if (isServerError(error)) {
      switch (error.response.status) {
        case 401:
        case 403:
          resetError();
          redirectSigninPage();
          // FallbackRender 는 ReactElement 를 요구한다. 리다이렉트 중이라 그릴 게 없다.
          return <></>;
      }
    }
    return <Error500PageView data-sentry-element="Error500PageView" data-sentry-component="fallback" data-sentry-source-file="GlobalErrorBoundary.tsx" />;
  };
  return <ErrorBoundary fallback={fallback} beforeCapture={scope => {
    scope.setTag('globalErrorBoundaryRendered', true);
  }} onError={e => {
    const isNetworkError = setApiNetworkLoggerWeb(e);
    if (isNetworkError) {
      logger.track('on_network_error');
      return;
    }
    console.error('Error Boundary:', e);
    // 대시보드 호환: error_captured(auto)와 별도로 user-facing boundary 도달을 표시
    logger.debug('enter_error_boundary', {
      errorName: e instanceof Error ? e.name : 'Unknown'
    });
    logger.captureError(e, {
      tags: {
        source: 'GlobalErrorBoundary'
      }
    });
  }} data-sentry-element="ErrorBoundary" data-sentry-component="GlobalErrorBoundary" data-sentry-source-file="GlobalErrorBoundary.tsx">
      {children}
    </ErrorBoundary>;
};
export default GlobalErrorBoundary;
type Props = {
  children: ReactNode;
};
export interface ServerError<T = unknown, D = unknown> extends AxiosError<T, D> {
  response: AxiosResponse<T, D>;
}
export function isServerError<T>(error: unknown): error is ServerError<T> {
  return error != null && axios.isAxiosError(error) && typeof (error as ServerError<T>).response?.status !== 'undefined';
}